import { useAppDispatch } from "../../../app/hooks";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/user/userSlice";
import { anyPermission, PermissionId } from "../../../app/permissions";
import { Avatar, Box, Button, ButtonGroup, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Stack, Tooltip, PaletteColor, Theme } from "@mui/material";
import PrizaAvatarGrid, { PrizaAvatarGridItem } from "../../../features/base/grid/PrizaAvatarGrid";
import { assertThumbnailUrl, LoadState } from "../../../redux/common";
import { useEffect, useMemo, useState } from "react";
import { isTokenReady } from "../../../features/user/UserAvatar";
import { retrieveRoutineItemList, routineItemListSlice, selectRoutineItemList } from "../../../redux/routine/routineItemListSlice";
import ObjectInsUpd from "../../../features/base/page/ObjectInsUpd";
import { emptyRoutineItem, routineItemInsert, routineItemInsUpdSlice, routineItemUpdate, routineItemRemove, selectRoutineItemInsUpd } from "../../../redux/routine/routineItemInsUpdSlice";
import { RoutineItemInsUpdData } from "./RoutineItemInsUpdData";
import { RoutineItemInsUpdReview } from "./RoutineItemInsUpdReview";

import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { retrieveRoutineItemOptionList, routineItemOptionListSlice, selectRoutineItemOptionList } from "../../../redux/routine/routineItemOptionListSlice";
import { emptyRoutineItemOption, routineItemOptionInsert, routineItemOptionInsUpdSlice, routineItemOptionUpdate, routineItemOptionRemove, selectRoutineItemOptionInsUpd } from "../../../redux/routine/routineItemOptionInsUpdSlice";
import { RoutineItemOptionInsUpdData } from "./RoutineItemOptionInsUpdData";
import { RoutineItemOptionInsUpdReview } from "./RoutineItemOptionInsUpdReview";
import { RoutineItemInfo } from "../../../redux/routine/routineItemSlice";
import { canEditRoutine } from "./Routine";

// export const getItemOptionColor = (color?: number) => {
export function getItemOptionColor(theme: Theme, color?: number): PaletteColor {
    switch (color) {
        case 1: return theme.palette.success;
        case 2: return theme.palette.error;
        case 3: return theme.palette.info;
        case 4: return theme.palette.warning;
        default: return theme.palette.primary;
    }
}

interface ContentListProps {
    schoolId: string;
    routineId: string;
}

const RoutineItemList = ({ schoolId, routineId }: ContentListProps) => {
    const dispatch = useAppDispatch();
    const routineItems = useSelector(selectRoutineItemList);
    const routineItemOptions = useSelector(selectRoutineItemOptionList);
    const routineItemInsUpd = useSelector(selectRoutineItemInsUpd);
    const routineItemOptionInsUpd = useSelector(selectRoutineItemOptionInsUpd);
    const user = useSelector(selectUser);
    const [isInsertingItem, setIsInsertingItem] = useState(false);
    const [updatingRoutineItemId, setUpdatingRoutineItemId] = useState(undefined as undefined | string);
    const [isInsertingItemOption, setIsInsertingItemOption] = useState(false);
    const [updatingRoutineItemOptionId, setUpdatingRoutineItemOptionId] = useState(undefined as undefined | string);
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [routineItemToRemove, setRoutineItemToRemove] = useState<undefined | RoutineItemInfo>(undefined);
    const [removeOptionDialogOpen, setRemoveOptionDialogOpen] = useState(false);
    const [routineItemOptionToRemove, setRoutineItemOptionToRemove] = useState<any>(undefined);

    const canEdit = canEditRoutine(user, schoolId);
    
    const request = useMemo(() => ({ schoolId, routineId }), [schoolId, routineId]);
    useEffect(() => {
        dispatch(routineItemListSlice.actions.check(request));
        if (routineItems.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveRoutineItemList(request));
        }
    }, [request, routineItems]);

    useEffect(() => {
        dispatch(routineItemOptionListSlice.actions.check(request));
        if (routineItemOptions.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveRoutineItemOptionList(request));
        }
    }, [request, routineItemOptions]);

    const addRoutineItem = () => {
        const newItem = {
            ...emptyRoutineItem(),
            routineId: routineId,
            schoolId: schoolId
        };
        dispatch(routineItemInsUpdSlice.actions.set(newItem));
        setIsInsertingItem(true);
    };

    const steps = [
        {
            name: "Dados do item da rotina",
            component: <RoutineItemInsUpdData />,
            check: () => routineItemInsUpd.routineItem.name.length > 2,
        },
        {
            name: "Revisão",
            component: <RoutineItemInsUpdReview />,
        }
    ];

    const stepsOption = [
        {
            name: "Dados da opção",
            component: <RoutineItemOptionInsUpdData />,
            check: () => routineItemOptionInsUpd.routineItemOption.name.length > 1,
        },
        {
            name: "Revisão",
            component: <RoutineItemOptionInsUpdReview />,
        }
    ];

    const alternativeContent = isInsertingItem || updatingRoutineItemId
        ? <ObjectInsUpd 
            name="Item de Rotina" 
            submit={() => {
                (updatingRoutineItemId)
                    ? dispatch(routineItemUpdate({routineItemId: updatingRoutineItemId, routineItem: routineItemInsUpd.routineItem}))
                    : dispatch(routineItemInsert({routineItem: routineItemInsUpd.routineItem}));
            }} 
            backTo={() => {
                setIsInsertingItem(false);
                setUpdatingRoutineItemId(undefined);
            }}
            backCleanup={() => {
                dispatch(routineItemListSlice.actions.clean());
                dispatch(routineItemInsUpdSlice.actions.clean());
            }}
            state={routineItemInsUpd.state} 
            steps={steps}
            edit={updatingRoutineItemId !== undefined}
        />
        : isInsertingItemOption || updatingRoutineItemOptionId
        ? <ObjectInsUpd 
            name="Opção" 
            submit={() => {
                (updatingRoutineItemOptionId)
                    ? dispatch(routineItemOptionUpdate({routineItemOptionId: updatingRoutineItemOptionId, routineItemOption: routineItemOptionInsUpd.routineItemOption}))
                    : dispatch(routineItemOptionInsert({routineItemOption: routineItemOptionInsUpd.routineItemOption}));
            }} 
            backTo={() => {
                setIsInsertingItemOption(false);
                setUpdatingRoutineItemOptionId(undefined);
            }}
            backCleanup={() => {
                dispatch(routineItemOptionListSlice.actions.clean());
                dispatch(routineItemOptionInsUpdSlice.actions.clean());
            }}
            state={routineItemOptionInsUpd.state} 
            steps={stepsOption}
            edit={updatingRoutineItemOptionId !== undefined}
        />
        : null;

    
    const items = [...(routineItems.list || [])];
    items.sort((a, b) => {
        return (a.order || 999) - (b.order || 999);
    });
    const mapped: PrizaAvatarGridItem[] = items.map((item) => {
        return {
            image: assertThumbnailUrl(item.url),
            title: item.name + (item.order ? ` (#${item.order})` : ""),
            item: item,
            extra: <Stack direction="column" spacing={1} sx={{ m: 0, width: "450px" }}>
                {canEdit &&
                <ButtonGroup sx={{ m: 0 }} size='small'>
                    <Tooltip title="Editar Item">
                        <Button variant="outlined" onClick={() => {
                            setUpdatingRoutineItemId(item.id);
                            dispatch(routineItemInsUpdSlice.actions.set(item));
                        }}>
                            <EditIcon />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Remover Item">
                        <Button variant="outlined" color='error' onClick={() => {
                            setRoutineItemToRemove(item);
                            setRemoveDialogOpen(true);
                        }}>
                            <DeleteForeverIcon />
                        </Button>
                    </Tooltip>
                </ButtonGroup>
                }
                <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-start" }}>
                    {routineItemOptions.list && 
                        routineItemOptions.list
                            .filter((opt) => opt.routineItemId === item.id)
                            .sort((a, b) => (a.order || 99) - (b.order || 99))
                            .map(option => (
                        <Box key={option.id} sx={{ mx: "2px", my: 1, width: "145px" }}>
                            <Chip
                                sx={{ 
                                    width: "100%",  
                                    color: (theme) => getItemOptionColor(theme, option.color).main,
                                    border: (theme) => `1px solid ${getItemOptionColor(theme, option.color).main}`,
                                    backgroundColor: (theme) => getItemOptionColor(theme, option.color).contrastText,
                                }}
                                avatar={<Avatar alt={option.name} src={assertThumbnailUrl(option.url)} />}
                                label={<Box sx={{ width: "85px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{option.name}</Box>}
                                variant="filled"
                                onClick={() => {
                                    if (canEdit) {
                                        setUpdatingRoutineItemOptionId(option.id);
                                        dispatch(routineItemOptionInsUpdSlice.actions.set(option));
                                    }
                                }}
                                onDelete={() => {
                                    if (canEdit) {
                                        setRoutineItemOptionToRemove(option);
                                        setRemoveOptionDialogOpen(true);
                                    }
                                }}
                            />
                        </Box>
                    ))}
                    <Box key={"not-informed"} sx={{ mx: "2px", my: 1, width: "145px" }}>
                        <Chip
                            sx={{ width: "100%" }}
                            color="primary"
                            avatar={<Avatar alt="Não informado" src="/static/images/not-informed.jpg" />}
                            label={<Box sx={{ width: "103px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Não informado</Box>}
                            variant="filled"
                        />
                    </Box>
                    {canEdit &&
                    <Box key={"new-item"} sx={{ mx: "2px", my: 1, width: "50px" }}>
                        <Tooltip title="Adicionar Opção">
                            <Button variant="contained" size='small' color='success' onClick={() => {
                                const newItem = {
                                    ...emptyRoutineItemOption(),
                                    routineId: routineId,
                                    routineItemId: item.id,
                                    schoolId: schoolId
                                };
                                dispatch(routineItemOptionInsUpdSlice.actions.set(newItem));
                                setIsInsertingItemOption(true);
                            }}>
                                <AddIcon />
                            </Button>
                        </Tooltip>
                    </Box>
                    }
                </Box>
            </Stack>
        };
    });
    const perm: boolean = anyPermission(user.priza.info, schoolId, [ PermissionId.SCHOOL_ADMIN ]);
    return <>
        <PrizaAvatarGrid
            alternativeContent={alternativeContent}
            list={mapped}
            toolbar={{
                label: "Itens da Rotina",
                buttons: perm ? [
                    {
                        label: <Tooltip title="Inserir Novo Item" ><AddIcon /></Tooltip>,
                        click: () => { addRoutineItem(); }
                    }
                ] : []
            }}
            state={routineItems.state}
            retry={() => {
                dispatch(routineItemListSlice.actions.clean()); 
                dispatch(routineItemOptionListSlice.actions.clean());
            }}
            messages={{
                empty: "Nenhum item associado a esta rotina.",
                error: "Erro ao carregar itens da rotina."
            }}
        />
        <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)}>
            <DialogTitle>Remover Item da Rotina</DialogTitle>
            <DialogContent>
                Tem certeza que deseja remover o item <b>{routineItemToRemove?.name}</b>?
                <br />
                Esta ação irá afetar os itens já registados para os alunos e não pode ser desfeita.
            </DialogContent>
            <DialogActions>
                <Button variant='contained' onClick={() => setRemoveDialogOpen(false)}>Cancelar</Button>
                <Button variant='contained' color="error" onClick={() => {
                    if (routineItemToRemove) {
                        dispatch(routineItemRemove({ routineItem: routineItemToRemove }));
                    }
                    setRemoveDialogOpen(false);
                    setRoutineItemToRemove(undefined);
                }}>Remover</Button>
            </DialogActions>
        </Dialog>
        <Dialog open={removeOptionDialogOpen} onClose={() => setRemoveOptionDialogOpen(false)}>
            <DialogTitle>Remover Opção</DialogTitle>
            <DialogContent>
                Tem certeza que deseja remover a opção <b>{routineItemOptionToRemove?.name}</b>?
                <br />
                Esta ação irá afetar os itens já registados para os alunos e não pode ser desfeita.
            </DialogContent>
            <DialogActions>
                <Button variant='contained' onClick={() => setRemoveOptionDialogOpen(false)}>Cancelar</Button>
                <Button variant='contained' color="error" onClick={() => {
                    if (routineItemOptionToRemove) {
                        dispatch(routineItemOptionRemove({ routineItemOption: routineItemOptionToRemove }));
                    }
                    setRemoveOptionDialogOpen(false);
                    setRoutineItemOptionToRemove(undefined);
                }}>Remover</Button>
            </DialogActions>
        </Dialog>
    </>;

};

export default RoutineItemList;